import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Trophy, Award, Medal, MessageSquareCheck, ShieldCheck, ThumbsUp, Loader2, Gauge } from "lucide-react";
import { CivicLeaderboard } from "@/components/civic-leaderboard";

import { AccountBadge } from "@/components/account-badge";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/leaderboard")({
  head: () => ({ meta: [{ title: "Xamseen — Leaderboard" }] }),
  component: LeaderboardPage,
});

type Scope = "global" | "country" | "city";
type CategoryKey = "civic" | "contributor" | "solutionist" | "verified" | "helpful";

const CATEGORIES: { key: CategoryKey; label: string; metricLabel: string; icon: any }[] = [
  { key: "civic", label: "Civic Score", metricLabel: "civic score", icon: Gauge },
  { key: "contributor", label: "Top Contributors", metricLabel: "reputation", icon: Trophy },
  { key: "solutionist", label: "Top Solutionists", metricLabel: "accepted solutions", icon: MessageSquareCheck },
  { key: "verified", label: "Most Verified", metricLabel: "verifications given", icon: ShieldCheck },
  { key: "helpful", label: "Most Helpful", metricLabel: "support received", icon: ThumbsUp },
];


interface Row {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  account_type?: any;
  verified?: boolean;
  is_founder?: boolean;
  level?: number;
  metric: number;
}

function LeaderboardPage() {
  const { profile } = useAuth();
  const [scope, setScope] = useState<Scope>("global");
  const [category, setCategory] = useState<CategoryKey>("civic");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const cityName = profile?.city?.trim() || null;
  const countryName = profile?.country?.trim() || null;

  useEffect(() => {
    if (category === "civic") { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {

        // Build scope filter on profiles
        const scopeFilter = (q: any) => {
          if (scope === "city" && cityName) return q.eq("city", cityName);
          if (scope === "country" && countryName) return q.eq("country", countryName);
          return q;
        };

        const profileCols = "id, username, full_name, avatar_url, account_type, verified, is_founder, level, reputation_points";

        if (category === "contributor") {
          const { data } = await scopeFilter(
            supabase.from("profiles").select(profileCols).order("reputation_points", { ascending: false }).limit(100)
          );
          if (cancelled) return;
          setRows((data ?? []).map((p: any) => ({ ...p, metric: p.reputation_points ?? 0 })));
        } else if (category === "solutionist") {
          // Aggregate accepted solutions per author
          const { data: comments } = await supabase
            .from("comments").select("author_id").eq("is_solution", true).limit(5000);
          const counts = new Map<string, number>();
          (comments ?? []).forEach((c: any) => counts.set(c.author_id, (counts.get(c.author_id) ?? 0) + 1));
          const topIds = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 200).map(([id]) => id);
          if (topIds.length === 0) { setRows([]); return; }
          const { data: profs } = await scopeFilter(
            supabase.from("profiles").select(profileCols).in("id", topIds)
          );
          if (cancelled) return;
          const mapped = (profs ?? []).map((p: any) => ({ ...p, metric: counts.get(p.id) ?? 0 }))
            .sort((a: Row, b: Row) => b.metric - a.metric).slice(0, 100);
          setRows(mapped);
        } else if (category === "verified") {
          const { data: reacts } = await supabase
            .from("reactions").select("user_id").eq("reaction", "verify" as any).limit(8000);
          const counts = new Map<string, number>();
          (reacts ?? []).forEach((r: any) => counts.set(r.user_id, (counts.get(r.user_id) ?? 0) + 1));
          const topIds = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 200).map(([id]) => id);
          if (topIds.length === 0) { setRows([]); return; }
          const { data: profs } = await scopeFilter(
            supabase.from("profiles").select(profileCols).in("id", topIds)
          );
          if (cancelled) return;
          const mapped = (profs ?? []).map((p: any) => ({ ...p, metric: counts.get(p.id) ?? 0 }))
            .sort((a: Row, b: Row) => b.metric - a.metric).slice(0, 100);
          setRows(mapped);
        } else if (category === "helpful") {
          // Sum upvotes across each author's posts
          const { data: posts } = await supabase
            .from("posts").select("author_id, upvotes").limit(10000);
          const sums = new Map<string, number>();
          (posts ?? []).forEach((p: any) => sums.set(p.author_id, (sums.get(p.author_id) ?? 0) + (p.upvotes ?? 0)));
          const topIds = Array.from(sums.entries()).sort((a, b) => b[1] - a[1]).slice(0, 200).map(([id]) => id);
          if (topIds.length === 0) { setRows([]); return; }
          const { data: profs } = await scopeFilter(
            supabase.from("profiles").select(profileCols).in("id", topIds)
          );
          if (cancelled) return;
          const mapped = (profs ?? []).map((p: any) => ({ ...p, metric: sums.get(p.id) ?? 0 }))
            .sort((a: Row, b: Row) => b.metric - a.metric).slice(0, 100);
          setRows(mapped);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, category, cityName, countryName]);

  const activeCategory = useMemo(() => CATEGORIES.find(c => c.key === category)!, [category]);
  const scopeUnavailable =
    (scope === "city" && !cityName) || (scope === "country" && !countryName);

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Rankings</h1>
          <p className="text-muted-foreground">Members making the biggest civic impact.</p>
        </header>

        {category !== "civic" && (
          <Tabs value={scope} onValueChange={(v) => setScope(v as Scope)}>
            <TabsList>
              <TabsTrigger value="global">🌍 Global</TabsTrigger>
              <TabsTrigger value="country" disabled={!countryName}>🏳 {countryName ?? "My Country"}</TabsTrigger>
              <TabsTrigger value="city" disabled={!cityName}>🏙 {cityName ?? "My City"}</TabsTrigger>
            </TabsList>
          </Tabs>
        )}


        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex w-max gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={cn(
                  "flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  category === c.key
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                )}
              >
                <c.icon className="h-3.5 w-3.5" />
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {category === "civic" ? (
          <CivicLeaderboard />
        ) : scopeUnavailable ? (

          <Card className="p-12 text-center text-sm text-muted-foreground">
            Set your {scope === "city" ? "city" : "country"} in settings to see local rankings.
          </Card>
        ) : loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <Card className="divide-y divide-border">
            {rows.map((u, i) => {
              const Icon = i === 0 ? Trophy : i === 1 ? Medal : i === 2 ? Award : null;
              return (
                <Link key={u.id} to="/profile/$username" params={{ username: u.username }} className="flex items-center gap-4 p-4 hover:bg-accent">
                  <div className={cn(
                    "grid h-8 w-8 place-items-center rounded-full text-sm font-semibold",
                    i === 0 ? "bg-yellow-400/20 text-yellow-700 dark:text-yellow-300"
                    : i === 1 ? "bg-slate-300/30 text-slate-700 dark:text-slate-200"
                    : i === 2 ? "bg-orange-400/20 text-orange-700 dark:text-orange-300"
                    : "bg-muted text-muted-foreground"
                  )}>{i + 1}</div>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={u.avatar_url ?? undefined} />
                    <AvatarFallback>{(u.full_name?.[0] || u.username[0] || "U").toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 font-medium">
                      <span className="truncate">{u.full_name || u.username}</span>
                      <AccountBadge accountType={u.account_type} verified={u.verified} isFounder={u.is_founder} />
                      {Icon && <Icon className="h-4 w-4 text-secondary shrink-0" />}
                    </div>
                    <div className="text-xs text-muted-foreground">@{u.username} · Lvl {u.level ?? 1}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-semibold text-primary tabular-nums">{u.metric.toLocaleString()}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{activeCategory.metricLabel}</div>
                  </div>
                </Link>
              );
            })}
            {rows.length === 0 && <div className="p-12 text-center text-muted-foreground">No data yet for this scope.</div>}
          </Card>
        )}
      </div>
    </AppShell>
  );
}
